import { z } from 'zod';

// ─── meta.json ───────────────────────────────────────────────────────────────

export const LayerConfigSchema = z.object({
  id: z.string(),
  group: z.string(),
  label: z.string(),
  type: z.enum(['choropleth', 'poi']),
  unit: z.string().optional(),
  field: z.string().optional(),
  stops: z.array(z.number()).optional(),
  palette: z.array(z.string()).optional(),
  inverse: z.boolean().optional(),
  /**
   * Ключ админ-уровня, если слой публикуется НЕ на базовой единице. Легальный
   * способ показать данные, доступные только крупнее: у Испании доход есть по
   * провинциям и не существует по муниципалитетам, а раскатывать провинциальное
   * среднее вниз запрещено — это ложная детализация.
   */
  level: z.string().optional(),
  src: z.string().optional(),
  icon: z.string().optional(),
  color: z.string().optional(),
});

/**
 * Пресет фильтров, объявленный страной в meta.json.
 *
 * Существует потому, что пресеты — это редакционное утверждение о стране, а не
 * о продукте: «Доломиты» и «Субальпийский» осмысленны только в Италии. Страна
 * без своего списка получает встроенный итальянский (features/filters/presets-config.ts).
 *
 * `filters` — свободная карта чисел; фронтенд берёт из неё только те ключи,
 * которые действительно есть в модели фильтров, остальные молча игнорирует.
 * Пресет с неизвестным фронтенду `id` безопасен: он попадёт в URL и аналитику
 * как есть, а поиск по id вернёт undefined и применение станет no-op.
 */
export const MetaFilterPresetSchema = z.object({
  id: z.string(),
  emoji: z.string(),
  label_en: z.string(),
  label_ru: z.string(),
  filters: z.record(z.string(), z.number()),
});

/**
 * Термины админ-единиц по умолчанию — итальянские.
 *
 * Италия исторически публикует meta.json без русских вариантов, и подставлять
 * вместо них пустую строку нельзя: интерфейс тогда скажет «Всего: 7 896 ·».
 * Значения ровно те, которыми продукт называл итальянские единицы до появления
 * второй страны.
 */
export const ADMIN_TERMS_FALLBACK = {
  place: 'comune',
  place_plural: 'comuni',
  place_ru: 'коммуна',
  place_plural_ru: 'коммуны',
  admin1: 'provincia',
  admin1_ru: 'провинция',
  admin0_region: 'regione',
} as const;

export const MetaSchema = z.object({
  version: z.string(),
  generated: z.string(),
  bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
  center: z.tuple([z.number(), z.number()]),
  zoom: z.number(),
  // ВНИМАНИЕ: это поле НЕ управляет тем, что видит пользователь. Его не читает
  // ни одна строка фронтенда — переключатель слоёв определяет наличие данных по
  // самим данным (useAvailableLayers). Здесь лежит декларация конвейера: гейт
  // сверяет её с собранным и не даёт опубликовать обещанный, но пустой слой.
  // У Италии список исторический — snow_days там, где каталог зовёт слой snow.
  layers: z.array(LayerConfigSchema),
  total: z.number(),
  available: z.record(z.string(), z.boolean()).optional(),

  // Country-agnostic foundation (F1.17). Все опциональны — старые meta.json без полей работают.
  country: z.string().length(2).default('it'),
  country_label: z.string().default('Italia'),
  currency: z.string().default('EUR'),
  currency_symbol: z.string().default('€'),
  distance_unit: z.string().default('km'),
  locale: z.string().default('it-IT'),
  admin_terms: z
    .object({
      place: z.string().default(ADMIN_TERMS_FALLBACK.place),
      place_plural: z.string().default(ADMIN_TERMS_FALLBACK.place_plural),
      place_ru: z.string().default(ADMIN_TERMS_FALLBACK.place_ru),
      place_plural_ru: z.string().default(ADMIN_TERMS_FALLBACK.place_plural_ru),
      admin1: z.string().default(ADMIN_TERMS_FALLBACK.admin1),
      admin1_ru: z.string().default(ADMIN_TERMS_FALLBACK.admin1_ru),
      admin0_region: z.string().default(ADMIN_TERMS_FALLBACK.admin0_region),
    })
    .default({ ...ADMIN_TERMS_FALLBACK }),

  /**
   * Пресеты фильтров страны. Поля нет ⇒ показывается встроенный итальянский
   * список — так карта Италии остаётся ровно такой, какой была.
   */
  filter_presets: z.array(MetaFilterPresetSchema).optional(),

  /**
   * id слоёв, по которым у страны РЕАЛЬНО есть значения. Панель фильтров
   * прячет контролы полей, которых здесь нет: фильтр по отсутствующему полю
   * не «ничего не отсекает», а отсекает всё (единица без значения не
   * удовлетворяет активному фильтру — см. features/filters/filter-fns.ts),
   * и пользователь видит пустую карту вместо продукта.
   *
   * Поля нет ⇒ ограничений нет, рендерятся все контролы (случай Италии).
   */
  filterable_fields: z.array(z.string()).optional(),
});

// ─── comuni_light.json ───────────────────────────────────────────────────────

export const ComuneLightSchema = z.object({
  istat: z.string(),
  name: z.string(),
  lat: z.number(),
  lon: z.number(),
  alt: z.number(),
  pop: z.number(),
  // Классификация рельефа задаётся страной (у Италии — zona altimetrica ISTAT).
  // Закрытый enum сделал бы датасет любой другой страны непарсибельным, поэтому
  // валидируем как строку; допустимые значения страны живут в её конфиге.
  // zone/reg_code/snow — итальянские поля. У страны без классификации рельефа
  // или без снежной статистики их нет, и подставлять ноль нельзя: ноль снежных
  // дней — это утверждение, а не пропуск (§0.1 runbook'а).
  zone: z.string().optional(),
  prov: z.string(),
  reg_code: z.number().optional(),
  t_mean: z.number(),
  snow: z.number().optional(),
  density: z.number(),
});

// ─── comuni.json (full) ──────────────────────────────────────────────────────

export const ComuneFullSchema = z.object({
  istat: z.string(),
  name: z.string(),
  lat: z.number(),
  lon: z.number(),
  alt: z.number(),
  pop: z.number(),
  // Классификация рельефа задаётся страной (у Италии — zona altimetrica ISTAT).
  // Закрытый enum сделал бы датасет любой другой страны непарсибельным, поэтому
  // валидируем как строку; допустимые значения страны живут в её конфиге.
  zone: z.string().optional(),
  prov: z.string(),
  reg: z.string().optional(),
  reg_code: z.number().optional(),
  // Intermediate district (between provincia and comune): SLL nationally,
  // comunità comprensoriale in Alto Adige. Code like 'sll0404' / 'cc6537725'.
  // Optional — older data versions без поля деградируют (district layer пуст).
  dist: z.string().optional(),
  climate: z.object({
    t_mean: z.number(),
    t_jul: z.number(),
    t_jan: z.number(),
    hot_days: z.number().optional(),
    precip: z.number().optional(),
    hum: z.number().optional(),
    snow: z.number().optional(),
    sun: z.number().optional(),
    sunny: z.number().optional(),
    // Optional monthly arrays — present when pipeline includes them. Length 12.
    t_mean_m: z.array(z.number()).length(12).optional(),
    precip_m: z.array(z.number()).length(12).optional(),
    hum_m: z.array(z.number()).length(12).optional(),
    snow_m: z.array(z.number()).length(12).optional(),
    sunny_m: z.array(z.number()).length(12).optional(),
  }),
  infra: z.object({
    hosp: z.number(),
    pharm: z.number(),
    school: z.number(),
    train: z.number(),
    air: z.number(),
    super: z.number(),
    // Горнолыжные поля и слой интернета есть только у Италии: у первого нет
    // источника вне Альп, у второго — лицензии (см. DEFERRED_RISKS.md R1).
    ski: z.number().optional(),
    hut: z.number().optional(),
    ski_n: z.number().optional(),
    kinder: z.number().optional(),
    gym: z.number().optional(),
    cowork: z.number().optional(),
  }),
  internet: z
    .object({
      dl: z.number(),
      ul: z.number(),
      ping: z.number(),
    })
    .optional(),
  demo: z.object({
    density: z.number(),
  }),
  // Optional fields — не в текущих данных, появятся в будущих версиях pipeline
  econ: z
    .object({
      sale_eur_m2: z.number().nullable().optional(),
      rent_eur_m2y: z.number().nullable().optional(),
      income_eur: z.number().nullable().optional(),
    })
    .optional(),
  foreign: z
    .object({
      total: z.number().nullable().optional(),
      ru: z.number().nullable().optional(),
      ua: z.number().nullable().optional(),
      by: z.number().nullable().optional(),
      ru_pct: z.number().nullable().optional(),
      ua_pct: z.number().nullable().optional(),
      by_pct: z.number().nullable().optional(),
    })
    .optional(),
  tax: z
    .object({
      irpef_pct: z.number().nullable().optional(),
      irpef_esente: z.number().nullable().optional(),
    })
    .optional(),
});

/**
 * Boundary adapter between a country's published dataset and the in-memory model.
 *
 * The in-memory identifier is still called `istat` — a name inherited from the
 * first country. Renaming it touches 24 files and is a mechanical refactor of
 * its own; bundling it with launching a second country would make any breakage
 * ambiguous. So the mapping lives here, in one place, and the rename is a
 * separate step. Countries publish neutral names (`key`, `parent`); Italy's
 * files keep their historical ones and pass through untouched.
 */
function adaptRecord(value: unknown): unknown {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return value;
  const raw = value as Record<string, unknown>;
  if (!('key' in raw)) return value; // итальянский датасет — как есть
  // area в модели нет — zod и так отбросит лишнее, поэтому просто не трогаем.
  const { key, parent, reg, density, ...rest } = raw;
  const out: Record<string, unknown> = { ...rest, istat: key, prov: parent };
  if (typeof reg === 'string') out.reg = reg;
  // density у полного датасета живёт в demo.density, у лёгкого — наверху.
  if (density !== undefined) {
    out.density = density;
    if (!('demo' in out)) out.demo = { density };
  }
  return out;
}

const adaptArray = (value: unknown) => (Array.isArray(value) ? value.map(adaptRecord) : value);

export const ComuneFullArraySchema = z.preprocess(adaptArray, z.array(ComuneFullSchema));
export const ComuneLightArraySchema = z.preprocess(adaptArray, z.array(ComuneLightSchema));
